import React, { useEffect, useRef, useState } from "react";
import { API } from "@/lib/api";
import { PaperPlaneTilt, ArrowClockwise } from "@phosphor-icons/react";

const C = { card: "#111a2e", surface: "#0F1729", green: "#10B981", orange: "#FF7A00", text: "#F8FAFC", sub: "#94A3B8", border: "rgba(255,255,255,0.10)" };
const HEAD = { fontFamily: "'Outfit', sans-serif" };

const DEFAULT_GREETING =
  "Hi! I'm the iYeneh SI Receptionist. I answer calls, qualify leads and book appointments 24/7. How can I help — shall I book you in?";
const DEFAULT_QUICK = ["Book an appointment", "I run a business", "What can you do?"];
const REQUEST_TIMEOUT_MS = 30000;

// Live, best-in-market SI Receptionist chat. Streams the reply token-by-token so
// the first words appear almost instantly, exactly like the phone line. Reused on
// the landing hero and the /receptionist page.
export const AIReceptionistChat = ({
  greeting = DEFAULT_GREETING,
  quickReplies = DEFAULT_QUICK,
  height = 440,
  className = "",
  style = {},
}) => {
  const [sid] = useState(() => Math.random().toString(36).slice(2));
  const [msgs, setMsgs] = useState([{ role: "ai", text: greeting }]);
  const [inp, setInp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const boxRef = useRef(null);
  const idRef = useRef(0);
  const lastUserRef = useRef(null);
  const busyRef = useRef(false);

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [msgs, busy, error]);

  // Core streaming turn. addUser=false is used by Retry (the user bubble already exists).
  const runStream = async (m, addUser) => {
    if (busyRef.current) return; // prevent duplicate submissions
    busyRef.current = true;
    setError(false);
    setBusy(true);
    lastUserRef.current = m;
    const aiId = `ai-${++idRef.current}`;
    setMsgs((s) => (addUser
      ? [...s, { role: "user", text: m }, { role: "ai", text: "", id: aiId }]
      : [...s, { role: "ai", text: "", id: aiId }]));

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), REQUEST_TIMEOUT_MS);
    let got = false;
    try {
      const resp = await fetch(`${API}/receptionist/demo-chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: m }),
        signal: ctrl.signal,
      });
      if (!resp.ok || !resp.body) throw new Error("bad response");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          let obj;
          try { obj = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (obj.delta) {
            got = true;
            setMsgs((s) => s.map((x) => (x.id === aiId ? { ...x, text: x.text + obj.delta } : x)));
          }
        }
      }
      if (!got) throw new Error("empty stream");
    } catch {
      // drop the empty placeholder bubble and surface a friendly error + retry
      setMsgs((s) => s.filter((x) => !(x.id === aiId && !x.text)));
      setError(true);
    } finally {
      clearTimeout(timer);
      busyRef.current = false;
      setBusy(false);
    }
  };

  const send = (text) => {
    const m = (text ?? inp).trim();
    if (!m || busyRef.current) return;
    setInp("");
    runStream(m, true);
  };

  const retry = () => {
    if (lastUserRef.current && !busyRef.current) runStream(lastUserRef.current, false);
  };

  const lastMsg = msgs[msgs.length - 1];
  const showTyping = busy && (!lastMsg || lastMsg.role !== "ai" || !lastMsg.text);

  return (
    <div
      data-testid="ai-receptionist-chat"
      className={`flex w-full flex-col overflow-hidden rounded-[1.75rem] border shadow-2xl ${className}`}
      style={{ borderColor: C.border, background: C.surface, ...style }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 border-b px-4 py-3.5" style={{ borderColor: C.border, background: "linear-gradient(120deg,#FF7A0018,transparent)" }}>
        <span className="relative grid h-10 w-10 place-items-center rounded-full text-sm font-black text-white" style={{ background: C.orange }}>
          SI
          <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-[#0F1729]" style={{ background: C.green }} />
        </span>
        <div className="min-w-0">
          <p style={HEAD} className="truncate text-sm font-bold text-white">iYeneh SI Receptionist</p>
          <p className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: C.green }}>
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full" style={{ background: C.green }} /> Online · answers &amp; books instantly
          </p>
        </div>
      </div>

      {/* Messages */}
      <div ref={boxRef} data-testid="ai-receptionist-messages" className="flex-1 space-y-2.5 overflow-y-auto p-4" style={{ minHeight: height, maxHeight: height }}>
        {msgs.map((mm, i) => (
          <div key={mm.id || i} className={`flex ${mm.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className="max-w-[82%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed text-white shadow"
              style={{
                background: mm.role === "user" ? C.orange : C.card,
                borderBottomRightRadius: mm.role === "user" ? 4 : undefined,
                borderBottomLeftRadius: mm.role === "ai" ? 4 : undefined,
              }}
            >
              {mm.text}
            </div>
          </div>
        ))}
        {showTyping && (
          <div className="flex justify-start">
            <div className="flex items-center gap-1 rounded-2xl px-3.5 py-3" style={{ background: C.card }}>
              {[0, 1, 2].map((d) => (
                <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full" style={{ background: C.sub, animationDelay: `${d * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
        {error && (
          <div className="flex flex-col items-start gap-2" data-testid="ai-receptionist-error">
            <div className="max-w-[82%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed text-white shadow" style={{ background: C.card }}>
              Sorry, I had trouble responding just now. Please try again.
            </div>
            <button
              data-testid="ai-receptionist-retry"
              onClick={retry}
              className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-white/5"
              style={{ borderColor: C.border }}
            >
              <ArrowClockwise size={14} weight="bold" /> Retry
            </button>
          </div>
        )}
      </div>

      {/* Quick replies */}
      {quickReplies?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-1">
          {quickReplies.map((q) => (
            <button
              key={q}
              data-testid="ai-receptionist-quick"
              onClick={() => send(q)}
              disabled={busy}
              className="rounded-full border px-3 py-1.5 text-xs font-medium transition-colors hover:bg-white/5 disabled:opacity-50"
              style={{ borderColor: C.border, color: C.sub }}
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="flex items-center gap-2 border-t p-3" style={{ borderColor: C.border }}>
        <input
          data-testid="ai-receptionist-input"
          value={inp}
          onChange={(e) => setInp(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          disabled={busy}
          placeholder="Type your message…"
          className="flex-1 rounded-full px-4 py-2.5 text-sm text-white outline-none disabled:opacity-60"
          style={{ background: C.card }}
        />
        <button
          data-testid="ai-receptionist-send"
          onClick={() => send()}
          disabled={busy}
          aria-label="Send"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full text-white transition-transform hover:-translate-y-0.5 disabled:opacity-50"
          style={{ background: C.orange }}
        >
          <PaperPlaneTilt size={18} weight="fill" />
        </button>
      </div>
    </div>
  );
};

export default AIReceptionistChat;
